/**
 * The two dialogs this template can raise: leaving the page and the low-time warning.
 *
 * They own their styling rather than borrowing from `ui.tsx`: a dialog is the one place where the
 * weight of a button carries meaning — the destructive action must read as weaker than the one that
 * keeps the client on the page.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LION_GREEN } from './ui';
import type { CancelReasonOption } from '../../../services/types';
import { cancelReasonKey } from '../../../services/cancelReasons';

// Причины — необязательная зона, и выглядеть она должна второстепенной: отделена чертой, мельче
// основного текста и тише кнопок. Иначе глаз цепляется за шесть одинаковых элементов и не находит,
// где здесь решение.
const ReasonHint = styled.p`
  margin: 20px 0 10px;
  padding-top: 16px;
  border-top: 1px solid #eeeeee;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #9a9a9a;
  text-align: center;
`;

const ReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const ReasonChip = styled.button<{ $picked: boolean }>`
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid ${({ $picked }) => ($picked ? LION_GREEN : '#e2e2e2')};
  background: ${({ $picked }) => ($picked ? 'rgba(106, 170, 58, 0.12)' : '#fafafa')};
  color: ${({ $picked }) => ($picked ? LION_GREEN : '#666')};
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
`;

// ─── Leave-confirm modal ──────────────────────────────────────────────────────
const LCOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const LCBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  max-width: calc(100vw - 32px);
  background-color: #fff;
  border-radius: 16px;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 11px 15px -7px,
    rgba(0, 0, 0, 0.14) 0px 24px 38px 3px,
    rgba(0, 0, 0, 0.12) 0px 9px 46px 8px;
  padding: 28px 24px 20px;
  white-space: pre-wrap;
  box-sizing: border-box;
`;

const LCText = styled.p`
  margin: 8px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.45;
  color: #555;
  letter-spacing: 0.00938em;
  text-align: center;
`;

// CPAYMENT-1730: у варианта «уже оплатил» три исхода — в строку они не помещаются.
const LCBtnColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: 24px;
`;

const lcBtnBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  appearance: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  text-transform: none;
  outline: 0;
  border: 0;
  margin: 0;
  text-decoration: none;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1),
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              color 250ms cubic-bezier(0.4, 0, 0.2, 1);
`;

// Действия выстроены по весу, а не по цвету. Главное — остаться и платить: залитая кнопка, первая
// и в фокусе. Выход к мерчанту — обводка. Отмена — тихая текстовая кнопка внизу: она нужна, но не
// должна быть тем, во что палец попадает первым.
const DlgActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin-top: 20px;
`;

const DlgPrimary = styled.button`
  ${lcBtnBase}
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background-color: ${LION_GREEN};
  &:hover { background-color: #5c9430; }
`;

const DlgSecondary = styled.button`
  ${lcBtnBase}
  width: 100%;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 15px;
  color: #333;
  border: 1px solid #d5d5d5;
  &:hover { background-color: rgba(0, 0, 0, 0.03); }
`;

const DlgDanger = styled.button`
  ${lcBtnBase}
  width: 100%;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #c62828;
  &:hover { background-color: rgba(198, 40, 40, 0.05); }
`;

const LCBtnCancel = styled.button`
  ${lcBtnBase}
  color: rgb(25, 118, 210);
  &:hover { background-color: rgba(25, 118, 210, 0.04); }
`;

/**
 * CPAYMENT-1730: после «Я оплатил» уход со страницы перестаёт быть отменой заявки. Отмену не
 * отнимаем — клиент мог передумать и не платить, — но она становится отдельной кнопкой рядом с
 * возвратом к мерчанту, а не побочным эффектом кнопки «Покинуть страницу».
 */
export const GateLeaveModal = ({
  onLeave,
  onCancel,
  receiptIntent,
  onLeaveWithoutCancel,
  reasons,
}: {
  onLeave: (reason?: CancelReasonOption) => void;
  onCancel: () => void;
  receiptIntent?: boolean;
  onLeaveWithoutCancel?: () => void;
  reasons?: CancelReasonOption[];
}) => {
  const { t } = useTranslation();
  // The pick stays in the dialog until the client actually cancels; a second tap clears it.
  const [picked, setPicked] = useState<CancelReasonOption | null>(null);
  const leave = () => onLeave(picked ?? undefined);

  const survey = reasons && reasons.length > 0 && (
    <>
      <ReasonHint>{t('cancelReason.hint')}</ReasonHint>
      <ReasonRow>
        {reasons.map((reason) => (
          <ReasonChip
            key={reason.id}
            type='button'
            $picked={picked?.id === reason.id}
            data-sentry-element='onepage-cancel-reason'
            onClick={() => setPicked(picked?.id === reason.id ? null : reason)}
          >
            {t(cancelReasonKey(reason.code))}
          </ReasonChip>
        ))}
      </ReasonRow>
    </>
  );

  if (receiptIntent && onLeaveWithoutCancel) {
    return (
      <LCOverlay onClick={onCancel}>
        <LCBox onClick={e => e.stopPropagation()}>
          <LCTitle>{t('gate.cancelTitle')}</LCTitle>
          <LCText>{t('gate.cancelText')}</LCText>
          {survey}
          <DlgActions>
            <DlgPrimary autoFocus data-sentry-element='onepage-leave-stay' onClick={onCancel}>{t('gate.keepPaying')}</DlgPrimary>
            <DlgSecondary data-sentry-element='onepage-leave-to-merchant' onClick={onLeaveWithoutCancel}>{t('modal.leaveToMerchant')}</DlgSecondary>
            <DlgDanger data-sentry-element='onepage-cancel-ticket' onClick={leave}>{t('modal.cancelTicket')}</DlgDanger>
          </DlgActions>
        </LCBox>
      </LCOverlay>
    );
  }

  return (
    <LCOverlay onClick={onCancel}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCTitle>{t('gate.cancelTitle')}</LCTitle>
        <LCText>{t('gate.cancelText')}</LCText>
        {survey}
        {/* «Покинуть страницу» здесь на деле отменяет оплату — и называется теперь так же. */}
        <DlgActions>
          <DlgPrimary autoFocus data-sentry-element='onepage-leave-cancel' onClick={onCancel}>{t('gate.keepPaying')}</DlgPrimary>
          <DlgDanger data-sentry-element='onepage-leave-confirm' onClick={leave}>{t('gate.cancelAction')}</DlgDanger>
        </DlgActions>
      </LCBox>
    </LCOverlay>
  );
};

const LCTitle = styled.p`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 700;
  font-size: 21px;
  line-height: 1.3;
  color: #1c1c1c;
  text-align: center;
`;

const LCBtnPrimary = styled.button`
  ${lcBtnBase}
  color: #fff;
  background-color: ${LION_GREEN};
  padding: 10px 16px;
  &:hover { background-color: #5c9430; }
`;

/**
 * CPAYMENT-1731: за 2 минуты до конца окна. Кнопка ведёт туда же, куда «Я оплатил» в флоу с
 * чеком: отметка «клиент платит» продлевает окно, страница открывает загрузку чека.
 */
export const GateLowTimeModal = ({
  onExtend,
  onDismiss,
}: {
  onExtend: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <LCOverlay onClick={onDismiss}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCTitle>{t('lowTime.title', 'Времени на оплату почти не осталось')}</LCTitle>
        <LCText>
          {t('lowTime.text', 'Если вы уже перевели деньги или переводите их сейчас — нажмите «Мне нужно ещё время», и мы продлим оплату, а затем попросим загрузить чек.')}
        </LCText>
        <LCBtnColumn>
          <LCBtnPrimary data-sentry-element='onepage-extend-time' onClick={onExtend}>{t('lowTime.action', 'Мне нужно ещё время')}</LCBtnPrimary>
          <LCBtnCancel data-sentry-element='onepage-low-time-dismiss' onClick={onDismiss}>{t('lowTime.dismiss', 'Закрыть')}</LCBtnCancel>
        </LCBtnColumn>
      </LCBox>
    </LCOverlay>
  );
};

